import { Link, Outlet, useParams } from "react-router-dom";
import { PencilLine } from "lucide-react";

import Button from "@/components/buttons/Button";
import {
    isEditableStatus,
    isReturnedForCorrection,
    RETURNED_FOR_CORRECTION_LABEL,
} from "@/utils/application-status";
import { useGetLicenceApprovalStatus, useGetLicenseDetails } from "../../repositories/licences-repository";
import { useGetInvestmentApprovalStatus, useGetInvestmentDetails } from "../../repositories/investment-repository";

/**
 * The "Edit application" affordance for a detail page.
 *
 * It is mounted as the route element that wraps the detail page rather than
 * placed inside it, so the detail fragments stay untouched. The queries it reads
 * are the same ones the detail page runs, under the same react-query keys, so
 * nothing extra is fetched.
 *
 * The action only appears while the application can actually be changed:
 * LicenseService.validateLicenseUpdateAllowed refuses an approved, rejected or
 * expired application with a 409, and offering an edit that is going to be
 * refused is worse than not offering one. Everything else is editable —
 * RETURNED_FOR_CORRECTION most of all, which is where the bar takes over the
 * amber styling of the notice below it and closes the loop the applicant is in:
 * read the reviewer's reason, edit, resubmit.
 */

type EditApplicationBarProps = {
    status?: string | null;
    /** RETURNED_FOR_CORRECTION only survives on the approval payload's currentStatus. */
    isReturned: boolean;
    editPath: string;
};

function EditApplicationBar({ status, isReturned, editPath }: EditApplicationBarProps) {
    if (!isEditableStatus(status)) return null;

    return (
        <div className="px-4 pt-3">
            <div
                className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3 ${
                    isReturned
                        ? "border-amber-300 bg-amber-50"
                        : "border-secondary-100 bg-white"
                }`}
            >
                <div>
                    {isReturned && (
                        <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">
                            {RETURNED_FOR_CORRECTION_LABEL}
                        </p>
                    )}
                    <p className="text-sm font-semibold text-[#2C2C2C]">
                        {isReturned
                            ? "Fix what the reviewer asked for, then resubmit"
                            : "Something to change on this application?"}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-600">
                        {isReturned
                            ? "Read the reason below, edit the application, then use Resubmit to send it back to the stage that returned it."
                            : "You can still edit it while it is being processed."}
                    </p>
                </div>

                <Link to={editPath}>
                    <Button
                        intent={isReturned ? "primary" : "secondary"}
                        size="md"
                        leftIcon={<PencilLine className="size-4" />}
                    >
                        Edit application
                    </Button>
                </Link>
            </div>
        </div>
    );
}

/** Route element: the edit action, then the licence detail page. */
export function LicenceDetailWithEdit() {
    const { applicationId } = useParams();
    const { licence_details } = useGetLicenseDetails(applicationId!);
    const { approval_status } = useGetLicenceApprovalStatus(applicationId!);

    const licence = licence_details?.results ?? licence_details;

    return (
        <div>
            <EditApplicationBar
                status={licence?.applicationStatus}
                isReturned={isReturnedForCorrection(approval_status?.results?.currentStatus)}
                editPath={`/applications/licences/applications-details/${applicationId}/edit`}
            />
            <Outlet />
        </div>
    );
}

/** Route element: the edit action, then the investment detail page. */
export function InvestmentDetailWithEdit() {
    const { applicationId } = useParams();
    const { investiment } = useGetInvestmentDetails(applicationId!);
    const { approval_status } = useGetInvestmentApprovalStatus(applicationId!);

    const investment = (investiment as any)?.results ?? investiment;

    return (
        <div>
            <EditApplicationBar
                status={investment?.applicationStatus}
                isReturned={isReturnedForCorrection(approval_status?.results?.currentStatus)}
                editPath={`/applications/investments/applications-details/${applicationId}/edit`}
            />
            <Outlet />
        </div>
    );
}
